import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    MenuItem,
    Modal,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import {
    Edit as PenIcon,
    Title as TextIcon,
    TrendingFlat as ArrowIcon,
    CropSquare as RectIcon,
    Undo as UndoIcon,
    BorderColor as HighlightIcon,
} from '@mui/icons-material';
import {
    Stage,
    Layer,
    Line,
    Image as KonvaImage,
    Text,
    Arrow,
    Rect,
    Transformer,
} from 'react-konva';
import {
    ANNOTATED_IMAGE_MIME_TYPE,
    ANNOTATED_IMAGE_QUALITY,
    DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE,
    fitImageWithinBounds,
    getAnnotationExportPixelRatio,
    getDataUrlByteSize,
    getImageDimensions,
    MAX_DEFICIENCY_IMAGE_BYTES,
} from './imageAnnotationUtils';

const DEFAULT_TEXT_BOX_WIDTH = 220;
const MIN_TEXT_INPUT_WIDTH = 100;
const TEXT_INPUT_EDGE_PADDING = 8;
const STAGE_VIEWPORT_WIDTH_RATIO = 0.9;
const STAGE_VIEWPORT_HEIGHT_RATIO = 0.7;

/**
 * Enhanced Image Annotator Modal Component
 *
 * Allows users to annotate images with multiple tools:
 * - Pen: Free-hand drawing
 * - Text: Add text boxes
 * - Arrow: Draw arrows
 * - Rectangle: Draw rectangles/boxes
 * - Highlighter: Semi-transparent drawing
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.imageUrl - URL of the image to annotate
 * @param {Function} props.onSave - Callback with annotated image dataURL
 */
function ImageAnnotatorModal({ open, onClose, imageUrl, onSave }) {
    // Tool selection state
    const [selectedTool, setSelectedTool] = useState('pen');

    // Annotation state
    const [lines, setLines] = useState([]); // For pen and highlighter
    const [texts, setTexts] = useState([]); // For text boxes
    const [arrows, setArrows] = useState([]); // For arrows
    const [rectangles, setRectangles] = useState([]); // For rectangles
    const [annotationHistory, setAnnotationHistory] = useState([]); // For undo

    // Drawing state
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentShape, setCurrentShape] = useState(null);

    // Text editing state (Snapchat-style)
    const [isEditingText, setIsEditingText] = useState(false);
    const [editingTextIndex, setEditingTextIndex] = useState(null);
    const [selectedTextIndex, setSelectedTextIndex] = useState(null);
    const [textInputValue, setTextInputValue] = useState('');
    const [textInputPosition, setTextInputPosition] = useState({ x: 0, y: 0 });
    const textInputRef = useRef(null);
    const textNodeRefs = useRef([]);
    const transformerRef = useRef(null);

    // Save state
    const [isSaving, setIsSaving] = useState(false);

    // Style settings
    const [strokeWidth, setStrokeWidth] = useState(3);
    const [strokeColor, setStrokeColor] = useState('#ff0000');
    const [fontSize, setFontSize] = useState(20);

    // Image and stage refs
    const stageRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const [image, setImage] = useState(null);
    const [stageSize, setStageSize] = useState({
        width: window.innerWidth * STAGE_VIEWPORT_WIDTH_RATIO,
        height: window.innerHeight * STAGE_VIEWPORT_HEIGHT_RATIO,
    });

    const fitLoadedImageToFreshBounds = useCallback((loadedImage) => {
        const { width: imageWidth, height: imageHeight } = getImageDimensions(loadedImage);
        const containerWidth = containerRef.current?.clientWidth;
        const maxWidth = containerWidth
            ? Math.min(containerWidth - 32, window.innerWidth * STAGE_VIEWPORT_WIDTH_RATIO)
            : window.innerWidth * STAGE_VIEWPORT_WIDTH_RATIO;
        const maxHeight = window.innerHeight * STAGE_VIEWPORT_HEIGHT_RATIO;

        setStageSize(fitImageWithinBounds(imageWidth, imageHeight, maxWidth, maxHeight));
    }, []);

    // Load the image
    useEffect(() => {
        if (!open || !imageUrl) {
            setImage(null);
            return undefined;
        }

        let cancelled = false;
        let imgFallback = null;

        // Do not leave the previous image available while a new one is loading.
        setImage(null);

        const img = new window.Image();
        // Set crossOrigin to avoid tainted canvas issues when loading from server
        img.crossOrigin = 'use-credentials';

        img.onload = () => {
            if (cancelled) return;

            setImage(img);
            fitLoadedImageToFreshBounds(img);
        };

        img.onerror = (error) => {
            if (cancelled) return;

            console.error('Error loading image for annotation:', error);
            // Try loading without credentials as fallback
            imgFallback = new window.Image();
            imgFallback.crossOrigin = 'anonymous';
            imgFallback.onload = () => {
                if (cancelled) return;

                setImage(imgFallback);
                fitLoadedImageToFreshBounds(imgFallback);
            };
            imgFallback.src = imageUrl;
        };

        img.src = imageUrl;

        return () => {
            cancelled = true;
            img.onload = null;
            img.onerror = null;

            if (imgFallback) {
                imgFallback.onload = null;
                imgFallback.onerror = null;
            }
        };
    }, [open, imageUrl, fitLoadedImageToFreshBounds]);

    // Reset all state when modal is closed
    useEffect(() => {
        if (!open) {
            setLines([]);
            setTexts([]);
            setArrows([]);
            setRectangles([]);
            setAnnotationHistory([]);
            setIsDrawing(false);
            setCurrentShape(null);
            setImage(null);
            setSelectedTool('pen');
            setIsEditingText(false);
            setEditingTextIndex(null);
            setSelectedTextIndex(null);
            setTextInputValue('');
            setStageSize({
                width: window.innerWidth * STAGE_VIEWPORT_WIDTH_RATIO,
                height: window.innerHeight * STAGE_VIEWPORT_HEIGHT_RATIO,
            });
        }
    }, [open]);

    // Handle tool change - set appropriate default sizes
    const handleToolChange = (event, newTool) => {
        if (!newTool) return;

        setSelectedTool(newTool);

        // Set default stroke width for highlighter
        if (newTool === 'highlighter') {
            setStrokeWidth(15); // Large size for highlighter
        } else if (
            strokeWidth === 15 &&
            (newTool === 'pen' || newTool === 'arrow' || newTool === 'rectangle')
        ) {
            // If switching from highlighter, reset to medium size for other tools
            setStrokeWidth(8);
        }

        // Set default color to black for text tool
        if (newTool === 'text') {
            setStrokeColor('#000000');
        }
    };

    // Auto-focus text input when it appears
    useEffect(() => {
        if (!isEditingText) return undefined;

        // Defer focus until after the input is painted; this is more reliable on desktop.
        const focusTimer = window.setTimeout(() => {
            if (!textInputRef.current) return;
            textInputRef.current.focus();
            const currentLength = textInputRef.current.value?.length || 0;
            if (typeof textInputRef.current.setSelectionRange === 'function') {
                textInputRef.current.setSelectionRange(currentLength, currentLength);
            }
        }, 0);

        return () => {
            window.clearTimeout(focusTimer);
        };
    }, [isEditingText]);

    useEffect(() => {
        const transformer = transformerRef.current;
        if (!transformer) return;

        const selectedTextNode =
            selectedTool === 'text' && selectedTextIndex !== null
                ? textNodeRefs.current[selectedTextIndex]
                : null;

        if (selectedTextNode && !isEditingText) {
            transformer.nodes([selectedTextNode]);
        } else {
            transformer.nodes([]);
        }

        transformer.getLayer()?.batchDraw();
    }, [selectedTextIndex, selectedTool, isEditingText, texts]);

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            if (image) fitLoadedImageToFreshBounds(image);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [image, fitLoadedImageToFreshBounds]);

    // Tool sizes
    const penSizes = { small: 3, medium: 8, large: 15 };
    const fontSizes = { small: 14, medium: 20, large: 28 };

    // Color options
    const colorOptions = [
        { color: '#ff0000', label: 'Red' },
        { color: '#00ff00', label: 'Green' },
        { color: '#0000ff', label: 'Blue' },
        { color: '#ffff00', label: 'Yellow' },
        { color: '#ff00ff', label: 'Pink' },
        { color: '#000000', label: 'Black' },
        { color: '#ffffff', label: 'White' },
    ];

    // Handle mouse/touch down
    const handlePointerDown = (e) => {
        if (isSaving) return;
        // If currently editing text, complete it before starting new action
        if (isEditingText) {
            handleTextInputComplete();
            return;
        }

        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();

        // Check if we clicked on a text/transformer element - don't create new text
        if (
            selectedTool === 'text' &&
            (e.target.className === 'Text' || e.target.getParent()?.className === 'Transformer')
        ) {
            return;
        }

        if (selectedTool === 'text') {
            setSelectedTextIndex(null);
        }

        if (selectedTool === 'pen' || selectedTool === 'highlighter') {
            setIsDrawing(true);
            const opacity = selectedTool === 'highlighter' ? 0.4 : 1;
            const newLine = {
                points: [pos.x, pos.y],
                color: strokeColor,
                strokeWidth,
                opacity,
            };
            setLines([...lines, newLine]);
        } else if (selectedTool === 'arrow') {
            setIsDrawing(true);
            setCurrentShape({ startX: pos.x, startY: pos.y, endX: pos.x, endY: pos.y });
        } else if (selectedTool === 'rectangle') {
            setIsDrawing(true);
            setCurrentShape({ x: pos.x, y: pos.y, width: 0, height: 0 });
        } else if (selectedTool === 'text') {
            // Snapchat-style text editing - show inline input
            setTextInputPosition({ x: pos.x, y: pos.y });
            setTextInputValue('');
            setIsEditingText(true);
            setEditingTextIndex(null);
        }
    };

    // Handle text input completion
    const handleTextInputComplete = () => {
        const trimmedText = textInputValue.trim();
        const isEditingExistingText = editingTextIndex !== null;
        const existingText = isEditingExistingText ? texts[editingTextIndex] : null;

        if (trimmedText) {
            const newText = {
                x: existingText ? existingText.x : textInputPosition.x,
                y: existingText ? existingText.y : textInputPosition.y,
                text: trimmedText,
                // Persist currently selected style when committing text edits.
                fontSize,
                color: strokeColor,
                width: existingText
                    ? existingText.width || DEFAULT_TEXT_BOX_WIDTH
                    : DEFAULT_TEXT_BOX_WIDTH,
                draggable: true,
            };

            if (isEditingExistingText) {
                // Editing existing text
                const updatedTexts = [...texts];
                updatedTexts[editingTextIndex] = newText;
                setTexts(updatedTexts);
                setSelectedTextIndex(editingTextIndex);
            } else {
                // Adding new text
                setTexts([...texts, newText]);
                setAnnotationHistory([...annotationHistory, { type: 'text', index: texts.length }]);
                setSelectedTextIndex(texts.length);
            }
        } else if (isEditingExistingText) {
            // Empty text on edit means remove the textbox.
            const updatedTexts = texts.filter((_, index) => index !== editingTextIndex);
            setTexts(updatedTexts);
            setSelectedTextIndex(null);
        }

        // Reset text editing state
        setIsEditingText(false);
        setEditingTextIndex(null);
        setTextInputValue('');
    };

    const handleTextClick = (index, e) => {
        e.cancelBubble = true;

        if (selectedTool !== 'text') return;

        const textItem = texts[index];
        if (!textItem) return;

        setSelectedTextIndex(index);
        setEditingTextIndex(index);
        setTextInputPosition({ x: textItem.x, y: textItem.y });
        setTextInputValue(textItem.text);
        setIsEditingText(true);
    };

    // Handle text drag
    const handleTextDragEnd = (index, e) => {
        const updatedTexts = [...texts];
        updatedTexts[index] = {
            ...updatedTexts[index],
            x: e.target.x(),
            y: e.target.y(),
        };
        setTexts(updatedTexts);
    };

    const handleTextTransformEnd = (index) => {
        const node = textNodeRefs.current[index];
        if (!node) return;

        const nextWidth = Math.max(80, node.width() * node.scaleX());
        node.scaleX(1);

        const updatedTexts = [...texts];
        updatedTexts[index] = {
            ...updatedTexts[index],
            x: node.x(),
            y: node.y(),
            width: nextWidth,
        };
        setTexts(updatedTexts);
    };

    // Handle mouse/touch move
    const handlePointerMove = (e) => {
        if (!isDrawing || isSaving) return;

        const stage = e.target.getStage();
        const point = stage.getPointerPosition();

        if (selectedTool === 'pen' || selectedTool === 'highlighter') {
            const lastLine = lines[lines.length - 1];
            lastLine.points = lastLine.points.concat([point.x, point.y]);
            setLines([...lines.slice(0, -1), lastLine]);
        } else if (selectedTool === 'arrow' && currentShape) {
            setCurrentShape({ ...currentShape, endX: point.x, endY: point.y });
        } else if (selectedTool === 'rectangle' && currentShape) {
            const width = point.x - currentShape.x;
            const height = point.y - currentShape.y;
            setCurrentShape({ ...currentShape, width, height });
        }
    };

    // Handle mouse/touch up
    const handlePointerUp = () => {
        if (!isDrawing) return;

        if (selectedTool === 'pen' || selectedTool === 'highlighter') {
            setAnnotationHistory([...annotationHistory, { type: 'line', index: lines.length - 1 }]);
        } else if (selectedTool === 'arrow' && currentShape) {
            const newArrow = {
                points: [
                    currentShape.startX,
                    currentShape.startY,
                    currentShape.endX,
                    currentShape.endY,
                ],
                color: strokeColor,
                strokeWidth,
            };
            setArrows([...arrows, newArrow]);
            setAnnotationHistory([...annotationHistory, { type: 'arrow', index: arrows.length }]);
            setCurrentShape(null);
        } else if (selectedTool === 'rectangle' && currentShape) {
            const newRect = {
                x: currentShape.x,
                y: currentShape.y,
                width: currentShape.width,
                height: currentShape.height,
                stroke: strokeColor,
                strokeWidth,
            };
            setRectangles([...rectangles, newRect]);
            setAnnotationHistory([
                ...annotationHistory,
                { type: 'rectangle', index: rectangles.length },
            ]);
            setCurrentShape(null);
        }

        setIsDrawing(false);
    };

    // Touch event handlers
    const handleTouchStart = (e) => {
        e.evt.preventDefault();
        handlePointerDown(e);
    };

    const handleTouchMove = (e) => {
        e.evt.preventDefault();
        handlePointerMove(e);
    };

    const handleTouchEnd = (e) => {
        e.evt.preventDefault();
        handlePointerUp();
    };

    // Undo last annotation
    const handleUndo = () => {
        if (annotationHistory.length === 0) return;

        const lastAction = annotationHistory[annotationHistory.length - 1];

        if (lastAction.type === 'line') {
            setLines(lines.slice(0, -1));
        } else if (lastAction.type === 'text') {
            setTexts(texts.slice(0, -1));
        } else if (lastAction.type === 'arrow') {
            setArrows(arrows.slice(0, -1));
        } else if (lastAction.type === 'rectangle') {
            setRectangles(rectangles.slice(0, -1));
        }

        setAnnotationHistory(annotationHistory.slice(0, -1));
    };

    // Save annotated image
    const handleSave = async () => {
        if (!stageRef.current || isSaving) return;

        setIsSaving(true);
        try {
            const hasAnnotations =
                lines.length > 0 || texts.length > 0 || arrows.length > 0 || rectangles.length > 0;

            if (!hasAnnotations) {
                await onSave(null, { hasAnnotations: false });
                return;
            }

            const { width: imageWidth, height: imageHeight } = getImageDimensions(image);
            const pixelRatio = getAnnotationExportPixelRatio({
                imageWidth,
                imageHeight,
                stageWidth: stageSize.width,
                stageHeight: stageSize.height,
            });
            const dataUrl = stageRef.current.toDataURL({
                pixelRatio,
                mimeType: ANNOTATED_IMAGE_MIME_TYPE,
                quality: ANNOTATED_IMAGE_QUALITY,
            });

            if (!dataUrl) {
                throw new Error('Failed to generate image data');
            }

            if (getDataUrlByteSize(dataUrl) > MAX_DEFICIENCY_IMAGE_BYTES) {
                const sizeError = new Error(DEFICIENCY_IMAGE_TOO_LARGE_MESSAGE);
                sizeError.isUserFacing = true;
                throw sizeError;
            }

            await onSave(dataUrl, { hasAnnotations: true });

            // Reset all state
            setLines([]);
            setTexts([]);
            setArrows([]);
            setRectangles([]);
            setAnnotationHistory([]);
            setIsDrawing(false);
            setCurrentShape(null);
        } catch (error) {
            console.error('Error saving annotated image:', error);
            alert(
                error.isUserFacing
                    ? error.message
                    : 'Failed to save annotated image. This may be due to a CORS issue. Please try again or contact support.'
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Cancel and close
    const handleCancel = () => {
        if (isSaving) return;
        setLines([]);
        setTexts([]);
        setArrows([]);
        setRectangles([]);
        setAnnotationHistory([]);
        setIsDrawing(false);
        setCurrentShape(null);
        onClose();
    };

    // Get current size value based on selected tool
    const getCurrentSize = () => {
        if (selectedTool === 'text') {
            return fontSize;
        }
        return strokeWidth;
    };

    // Get size options based on selected tool
    const getSizeOptions = () => {
        if (selectedTool === 'text') {
            return fontSizes;
        }
        return penSizes;
    };

    // Handle size change
    const handleSizeChange = (value) => {
        if (selectedTool === 'text') {
            setFontSize(value);
        } else {
            setStrokeWidth(value);
        }
    };

    const activeTextWidth =
        editingTextIndex !== null
            ? texts[editingTextIndex]?.width || DEFAULT_TEXT_BOX_WIDTH
            : DEFAULT_TEXT_BOX_WIDTH;
    const maxTextInputWidth = Math.max(
        MIN_TEXT_INPUT_WIDTH,
        stageSize.width - TEXT_INPUT_EDGE_PADDING * 2
    );
    const textInputWidth = Math.min(
        Math.max(MIN_TEXT_INPUT_WIDTH, activeTextWidth),
        maxTextInputWidth
    );
    const halfTextInputWidth = textInputWidth / 2;
    const minCenterX = halfTextInputWidth + TEXT_INPUT_EDGE_PADDING;
    const maxCenterX = Math.max(
        minCenterX,
        stageSize.width - halfTextInputWidth - TEXT_INPUT_EDGE_PADDING
    );
    const clampedTextInputX = Math.min(Math.max(textInputPosition.x, minCenterX), maxCenterX);

    const estimatedTextInputHeight = Math.max(44, fontSize + 24);
    const halfTextInputHeight = estimatedTextInputHeight / 2;
    const minCenterY = halfTextInputHeight + TEXT_INPUT_EDGE_PADDING;
    const maxCenterY = Math.max(
        minCenterY,
        stageSize.height - halfTextInputHeight - TEXT_INPUT_EDGE_PADDING
    );
    const clampedTextInputY = Math.min(Math.max(textInputPosition.y, minCenterY), maxCenterY);

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            aria-labelledby="annotate-image-modal"
            aria-describedby="modal-for-annotating-deficiency-images"
        >
            <Box
                ref={containerRef}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '80%' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: { xs: 1, sm: 2 },
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Toolbar */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Tool Selection */}
                    <ToggleButtonGroup
                        value={selectedTool}
                        exclusive
                        onChange={handleToolChange}
                        size="small"
                        disabled={isSaving}
                        sx={{
                            height: 40,
                            bgcolor: '#F5F8FA',
                            borderRadius: '8px',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '6px',
                                mx: 0.25,
                                color: '#5B6C7D',
                                '&.Mui-selected': {
                                    bgcolor: '#FFFFFF',
                                    color: '#0052FF',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        bgcolor: '#FFFFFF',
                                    },
                                },
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.5)',
                                },
                            },
                        }}
                    >
                        <ToggleButton value="pen" aria-label="pen">
                            <Tooltip title="Pen">
                                <PenIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="text" aria-label="text">
                            <Tooltip title="Text">
                                <TextIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="arrow" aria-label="arrow">
                            <Tooltip title="Arrow">
                                <ArrowIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="rectangle" aria-label="rectangle">
                            <Tooltip title="Rectangle">
                                <RectIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="highlighter" aria-label="highlighter">
                            <Tooltip title="Highlighter">
                                <HighlightIcon fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Color Selector */}
                    <FormControl
                        size="small"
                        sx={{ minWidth: { xs: 60, sm: 80 } }}
                        disabled={isSaving}
                    >
                        <Select
                            value={strokeColor}
                            onChange={(e) => setStrokeColor(e.target.value)}
                            sx={{ height: 36 }}
                            variant="outlined"
                            displayEmpty
                            renderValue={(value) => (
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        bgcolor: value,
                                        border:
                                            value === '#ffffff'
                                                ? '2px solid #666'
                                                : '1px solid #ccc',
                                        mx: 'auto',
                                    }}
                                />
                            )}
                        >
                            {colorOptions.map((option) => (
                                <MenuItem key={option.color} value={option.color}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                bgcolor: option.color,
                                                mr: 1,
                                                border:
                                                    option.color === '#ffffff'
                                                        ? '2px solid #666'
                                                        : '1px solid #ccc',
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Size Selector */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {Object.entries(getSizeOptions()).map(([size, value]) => {
                            const dotSize =
                                selectedTool === 'text'
                                    ? { small: 8, medium: 12, large: 16 }[size]
                                    : { small: 6, medium: 10, large: 14 }[size];

                            return (
                                <IconButton
                                    key={size}
                                    color={getCurrentSize() === value ? 'primary' : 'default'}
                                    size="small"
                                    disabled={isSaving}
                                    onClick={() => handleSizeChange(value)}
                                    sx={{
                                        height: 36,
                                        width: 36,
                                        bgcolor:
                                            getCurrentSize() === value
                                                ? 'rgba(25, 118, 210, 0.08)'
                                                : 'transparent',
                                        border:
                                            getCurrentSize() === value
                                                ? '1px solid rgba(25, 118, 210, 0.5)'
                                                : '1px solid rgba(0,0,0,0.12)',
                                    }}
                                >
                                    {selectedTool === 'text' ? (
                                        <Box
                                            sx={{
                                                fontSize: dotSize,
                                                fontWeight: 'bold',
                                                lineHeight: 1,
                                            }}
                                        >
                                            A
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: dotSize,
                                                height: dotSize,
                                                borderRadius: '50%',
                                                bgcolor: 'currentColor',
                                            }}
                                        />
                                    )}
                                </IconButton>
                            );
                        })}
                    </Box>

                    {/* Undo Button */}
                    <IconButton
                        onClick={handleUndo}
                        disabled={isSaving || annotationHistory.length === 0}
                        size="small"
                        sx={{ height: 36, width: 36 }}
                    >
                        <UndoIcon />
                    </IconButton>

                    {/* Spacer */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* Action Buttons */}
                    <Button
                        onClick={handleCancel}
                        size="small"
                        disabled={isSaving}
                        sx={{ minWidth: { xs: 60, sm: 64 } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        size="small"
                        disabled={isSaving}
                        sx={{ minWidth: { xs: 60, sm: 64 } }}
                    >
                        {isSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
                    </Button>
                </Box>

                {/* Canvas Area */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flex: 1,
                        overflow: 'auto',
                        touchAction: 'none',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        padding: 1,
                        position: 'relative',
                    }}
                >
                    {image && (
                        <Box sx={{ position: 'relative' }}>
                            <Stage
                                width={stageSize.width}
                                height={stageSize.height}
                                onMouseDown={handlePointerDown}
                                onMousemove={handlePointerMove}
                                onMouseup={handlePointerUp}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                ref={stageRef}
                                style={{ touchAction: 'none' }}
                            >
                                {/* Image Layer */}
                                <Layer>
                                    <KonvaImage
                                        ref={imageRef}
                                        image={image}
                                        width={stageSize.width}
                                        height={stageSize.height}
                                    />
                                </Layer>

                                {/* Annotations Layer */}
                                <Layer>
                                    {/* Lines (Pen & Highlighter) */}
                                    {lines.map((line, i) => (
                                        <Line
                                            key={`line-${i}`}
                                            points={line.points}
                                            stroke={line.color}
                                            strokeWidth={line.strokeWidth}
                                            opacity={line.opacity}
                                            tension={0.5}
                                            lineCap="round"
                                            lineJoin="round"
                                        />
                                    ))}

                                    {/* Arrows */}
                                    {arrows.map((arrow, i) => (
                                        <Arrow
                                            key={`arrow-${i}`}
                                            points={arrow.points}
                                            stroke={arrow.color}
                                            strokeWidth={arrow.strokeWidth}
                                            fill={arrow.color}
                                            pointerLength={10}
                                            pointerWidth={10}
                                        />
                                    ))}

                                    {/* Rectangles */}
                                    {rectangles.map((rect, i) => (
                                        <Rect
                                            key={`rect-${i}`}
                                            x={rect.x}
                                            y={rect.y}
                                            width={rect.width}
                                            height={rect.height}
                                            stroke={rect.stroke}
                                            strokeWidth={rect.strokeWidth}
                                        />
                                    ))}

                                    {/* Texts */}
                                    {texts.map((textItem, i) => (
                                        <Text
                                            key={`text-${i}`}
                                            ref={(node) => {
                                                textNodeRefs.current[i] = node;
                                            }}
                                            x={textItem.x}
                                            y={textItem.y}
                                            text={textItem.text}
                                            width={textItem.width || DEFAULT_TEXT_BOX_WIDTH}
                                            wrap="word"
                                            fontSize={textItem.fontSize}
                                            fill={textItem.color}
                                            fontFamily="Arial"
                                            fontStyle="bold"
                                            draggable
                                            onDragEnd={(e) => handleTextDragEnd(i, e)}
                                            onTransformEnd={() => handleTextTransformEnd(i)}
                                            onClick={(e) => handleTextClick(i, e)}
                                            onTap={(e) => handleTextClick(i, e)}
                                        />
                                    ))}

                                    <Transformer
                                        ref={transformerRef}
                                        enabledAnchors={['middle-left', 'middle-right']}
                                        rotateEnabled={false}
                                        keepRatio={false}
                                        boundBoxFunc={(oldBox, newBox) => {
                                            if (Math.abs(newBox.width) < 80) {
                                                return oldBox;
                                            }
                                            return newBox;
                                        }}
                                    />

                                    {/* Current shape being drawn */}
                                    {currentShape && selectedTool === 'arrow' && (
                                        <Arrow
                                            points={[
                                                currentShape.startX,
                                                currentShape.startY,
                                                currentShape.endX,
                                                currentShape.endY,
                                            ]}
                                            stroke={strokeColor}
                                            strokeWidth={strokeWidth}
                                            fill={strokeColor}
                                            pointerLength={10}
                                            pointerWidth={10}
                                        />
                                    )}

                                    {currentShape && selectedTool === 'rectangle' && (
                                        <Rect
                                            x={currentShape.x}
                                            y={currentShape.y}
                                            width={currentShape.width}
                                            height={currentShape.height}
                                            stroke={strokeColor}
                                            strokeWidth={strokeWidth}
                                        />
                                    )}
                                </Layer>
                            </Stage>

                            {/* Snapchat-style inline text input */}
                            {isEditingText && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: clampedTextInputX,
                                        top: clampedTextInputY,
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 1000,
                                        width: textInputWidth,
                                        maxWidth: maxTextInputWidth,
                                    }}
                                >
                                    <TextField
                                        inputRef={textInputRef}
                                        value={textInputValue}
                                        onChange={(e) => setTextInputValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleTextInputComplete();
                                            } else if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setIsEditingText(false);
                                                setTextInputValue('');
                                            }
                                        }}
                                        placeholder="Type text..."
                                        autoFocus
                                        multiline
                                        variant="standard"
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                fontSize: fontSize,
                                                color: strokeColor,
                                                fontWeight: 'bold',
                                                fontFamily: 'Arial',
                                                width: '100%',
                                                minWidth: `${MIN_TEXT_INPUT_WIDTH}px`,
                                                backgroundColor: 'rgba(255,255,255,0.9)',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                            },
                                            '& .MuiInput-underline:before': {
                                                borderBottom: 'none',
                                            },
                                            '& .MuiInput-underline:after': {
                                                borderBottom: `2px solid ${strokeColor}`,
                                            },
                                            '& .MuiInput-underline:hover:not(.Mui-disabled):before':
                                                {
                                                    borderBottom: 'none',
                                                },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
}

ImageAnnotatorModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    imageUrl: PropTypes.string,
    onSave: PropTypes.func.isRequired,
};

export default ImageAnnotatorModal;
